/**
 * @module pages/Order/InvoiceStatusPoller
 * @description Headless React component for polling invoice payment status.
 * Automatically polls invoice status and updates order state when status changes.
 * Continues polling even after PAID status since invoices can switch back to unpaid.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { LAMBDA_POLL_INTERVAL_INVOICE, useLambdaPoll } from 'src/modules/useLambda';
import { useOrders } from 'src/providers/OrdersProvider';
import { identityHeadersPromise } from 'src/providers/IdentityProvider';
import { InvoiceStatusPollerProps, InvoiceStatusRequest, InvoiceStatusResponse } from './types';

/**
 * @component InvoiceStatusPoller
 * @summary Headless component that polls invoice payment status and updates parent order state.
 * Continuously polls invoice status from provider API to detect payment status changes.
 * Unlike package polling, this never stops automatically since invoice status can revert.
 * @param {InvoiceStatusPollerProps} props component props
 * @returns {null} Renders nothing (headless component)
 */
export const InvoiceStatusPoller: React.FC<InvoiceStatusPollerProps> = ({ orderId, invoice, onReplaceOrder }) => {
  const { refreshOrder } = useOrders();
  const [headers, setHeaders] = useState<HeadersInit | null>(null);

  // Fetch identity headers once so lambda calls are authorized
  useEffect(() => {
    let mounted = true;
    identityHeadersPromise().then((h) => {
      if (mounted) setHeaders(h);
    });
    return () => {
      mounted = false;
    };
  }, []);

  const getPayload = useCallback<() => InvoiceStatusRequest>(() => ({
    type: 'status',
    data: { orderId, invoiceId: invoice.id },
  }), [orderId, invoice.id]);

  const poll = useLambdaPoll<InvoiceStatusRequest, InvoiceStatusResponse>({
    functionName: 'invoice',
    autoStart: false,
    intervalMs: LAMBDA_POLL_INTERVAL_INVOICE,
    getPayload,
    headers: headers || undefined,
  });

  const { isRunning, start, lastResult } = poll;

  // Auto-start polling when we have headers
  useEffect(() => {
    if (!headers) return;
    if (!isRunning) {
      start();
    }
  }, [headers, isRunning, start]);

  // On poll result, refresh the order
  // NOTE: Unlike package polling, we NEVER stop polling invoices automatically
  // because invoice status can switch back from PAID to unpaid
  useEffect(() => {
    if (!lastResult) return;
    (async () => {
      const fresh = await refreshOrder(orderId);
      if (fresh) {
        onReplaceOrder?.(fresh);
      }
    })();
  }, [lastResult, refreshOrder, orderId, onReplaceOrder]);

  return null;
};

export default InvoiceStatusPoller;
